/**
 * @file PlanConfirmSheet.tsx
 * @brief The confirmation before a plan is bought: exact charge, interval,
 *        the card it goes on; then 3D Secure if the bank asks, and "active"
 *        only once the server says so.
 *
 * @details
 * Steps (planConfirm.ts holds the words and the outcome rules):
 * 1. "Pay £17.00 now" with "Then £17.00 every month until you cancel."
 * 2. POST /billing/subscriptions with the default card; the server confirms
 *    the first payment itself and hands back a client secret only for 3DS.
 * 3. 3DS: confirmWithBank → `stripe.handleNextAction` (the bank's challenge in a frame, so it
 *    works in the Mac app too), then POST /subscriptions/sync, which re-reads
 *    Stripe. A page that only says "done" grants nothing.
 * 4. Success shows only for status `active`; anything else says what
 *    happened and where to fix it.
 * No card → "Add a card" opens the card sheet and comes back here.
 *
 * @author DES
 * @date 17-10-2026
 */

import { CircleCheck, CreditCard, LoaderCircle, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { confirmWithBank } from "@/shared/stripe/confirmWithBank";
import { cn } from "@/shared/theme/cn";
import { myBillingKeys, subscribeToPlan, syncMySubscription, useMyCards } from "../api";
import { billingErrorView, isFinalRefusal } from "../billingErrors";
import { billingPath } from "../billingPaths";
import { brandName } from "../cardRemoval";
import {
  buildPlanConfirmation,
  isServerActive,
  subscribeOutcome,
  type BillingInterval,
  type CatalogPlan,
} from "../planConfirm";
import type { SubscriptionStatus } from "../schemas";
import { AddMyCardSheet } from "./AddMyCardSheet";
import { WalletPayButton } from "./WalletPayButton";
import { price, vatMode } from "./format";

type Phase =
  | { kind: "confirm" }
  | { kind: "working"; note: string }
  | { kind: "done"; planName: string }
  | { kind: "error"; title: string; message: string; to?: string; toLabel?: string; final?: boolean };

const SYNC_RETRIES = 3;

async function syncUntilSettled(subscriptionId: string): Promise<SubscriptionStatus> {
  let last = await syncMySubscription(subscriptionId);
  for (let i = 1; i < SYNC_RETRIES && !isServerActive(last.subscription?.status); i++) {
    await new Promise((r) => setTimeout(r, 1500));
    last = await syncMySubscription(subscriptionId);
  }
  return last;
}

export function PlanConfirmSheet({
  plan,
  interval,
  onOpenChange,
}: {
  plan: CatalogPlan | null;
  interval: BillingInterval;
  onOpenChange: (open: boolean) => void;
}) {
  const open = plan != null;
  const cards = useMyCards();
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (open) setPhase({ kind: "confirm" });
  }, [open, plan?.code, interval]);

  const confirmation = plan ? buildPlanConfirmation(plan, interval, vatMode) : null;
  const defaultId = cards.data?.defaultPaymentMethod ?? null;
  const card = cards.data?.cards.find((c) => c.id === defaultId) ?? null;
  const busy = phase.kind === "working";

  const fail = (code: string | undefined, message: string) => {
    const view = billingErrorView(code, "personal", message);
    setPhase({
      kind: "error",
      title: view?.title ?? "Payment not completed",
      message: view?.message ?? message,
      to: view?.action?.to,
      toLabel: view?.action?.label,
      final: isFinalRefusal(code),
    });
  };

  /**
   * Pay with the default card, or with the PaymentMethod an Apple Pay /
   * Google Pay sheet just produced (18-10-2026). Same server path either way.
   */
  const pay = async (walletPaymentMethodId?: string): Promise<boolean> => {
    const paymentMethodId = walletPaymentMethodId ?? card?.id;
    if (!plan || !confirmation || !paymentMethodId) return false;
    setPhase({ kind: "working", note: "Taking payment…" });
    try {
      const res = await subscribeToPlan({ planCode: plan.code, interval, paymentMethodId });
      let outcome = subscribeOutcome(res);

      if (outcome.kind === "needs_action") {
        setPhase({ kind: "working", note: "Waiting for your bank to confirm…" });
        // One bank check at a time (confirmWithBank, 21-10-2026).
        const bank = await confirmWithBank(outcome.clientSecret, res.subscriptionId);
        if (!bank.ok) {
          fail(bank.code, bank.message);
          return false;
        }
        outcome = { kind: "pending", status: "incomplete" };
      }

      setPhase({ kind: "working", note: "Checking with Stripe…" });
      const status = await syncUntilSettled(res.subscriptionId);
      qc.setQueryData(myBillingKeys.status(), status);
      void qc.invalidateQueries({ queryKey: myBillingKeys.all });

      if (isServerActive(status.subscription?.status)) {
        setPhase({ kind: "done", planName: plan.name });
        return true;
      }
      fail(
        undefined,
        outcome.kind === "failed"
          ? "Stripe didn't accept the payment. Nothing was activated."
          : "The payment hasn't gone through yet, so the plan isn't active. Check your card, then try again.",
      );
      return false;
    } catch (err) {
      if (err instanceof ApiClientError) {
        fail(
          err.code,
          err.status === 409
            ? "You already have an active plan. Cancel it first; a new plan can start when it ends."
            : err.message,
        );
      } else {
        fail(undefined, err instanceof Error ? err.message : "Please try again.");
      }
      return false;
    }
  };

  return (
    <>
      <FormSheet open={open && !addOpen} onOpenChange={(v) => !busy && onOpenChange(v)}>
        <FormSheetContent side="palette">
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>{phase.kind === "done" ? "Plan active" : `Confirm ${confirmation?.heading ?? "plan"}`}</FormSheetTitle>
              <FormSheetDescription>
                {phase.kind === "done"
                  ? "Stripe confirmed the payment."
                  : "Check the amount and the card before you pay."}
              </FormSheetDescription>
            </FormSheetHeader>
            <FormSheetBody>
              {phase.kind === "done" ? (
                <FormSheetSection title="All set">
                  <div className="flex items-start gap-3 px-4 py-4 md:px-5">
                    <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                    <p className="text-sm">
                      {phase.planName} is active. The receipt is in{" "}
                      <Link to={billingPath("invoices")} className="font-medium underline underline-offset-2" onClick={() => onOpenChange(false)}>
                        Invoices
                      </Link>
                      .
                    </p>
                  </div>
                </FormSheetSection>
              ) : (
                <>
                  <FormSheetSection title="You pay">
                    <div className="px-4 py-4 md:px-5" data-testid="plan-confirm-amount">
                      <p className="text-2xl font-semibold tracking-tight">
                        {plan && confirmation ? price(confirmation.chargeMinor, plan.currency) : "—"}
                        <span className="ml-2 text-sm font-normal text-muted-foreground">today</span>
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{confirmation?.renewLine}</p>
                      {plan?.includedMinutes ? (
                        <p className="mt-2 text-sm">
                          Includes {plan.includedMinutes.toLocaleString("en-GB")} Intelligence minutes a month.
                        </p>
                      ) : null}
                    </div>
                  </FormSheetSection>
                  {plan && confirmation && phase.kind !== "working" ? (
                    <div className="px-4 pt-4 md:px-5">
                      <WalletPayButton
                        plan={plan}
                        interval={interval}
                        chargeMinor={confirmation.chargeMinor}
                        onPaymentMethod={(pm) => pay(pm)}
                      />
                    </div>
                  ) : null}
                  <FormSheetSection title="Card">
                    <div className="flex items-center justify-between gap-3 px-4 py-4 md:px-5">
                      {cards.isLoading ? (
                        <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />
                      ) : card ? (
                        <p className="flex items-center gap-2 text-sm">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          {brandName(card.brand)} •••• {card.last4}
                          <Link to={billingPath("cards")} className="ml-2 text-xs text-muted-foreground underline" onClick={() => onOpenChange(false)}>
                            Change
                          </Link>
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-muted-foreground">Add a card to pay for this plan.</p>
                          <Button size="sm" onClick={() => setAddOpen(true)} disabled={busy}>
                            Add a card
                          </Button>
                        </>
                      )}
                    </div>
                  </FormSheetSection>
                  {phase.kind === "error" ? (
                    <div className="flex items-start gap-3 border-b border-r border-border/60 bg-destructive/5 px-4 py-4 md:px-5" role="alert">
                      <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                      <div className="text-sm">
                        <p className="font-semibold">{phase.title}</p>
                        <p className="mt-0.5 text-muted-foreground">{phase.message}</p>
                        {phase.to ? (
                          <Link to={phase.to} onClick={() => onOpenChange(false)} className="mt-1 inline-block font-medium underline underline-offset-2">
                            {phase.toLabel}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </FormSheetBody>
            <FormSheetFooter>
              <FormSheetFooterCell>
                {phase.kind === "done" || (phase.kind === "error" && phase.final) ? (
                  <Button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                  >
                    {phase.kind === "done" ? "Done" : "Close"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={busy || !card || !confirmation}
                    onClick={() => void pay()}
                    className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                  >
                    {busy ? (
                      <>
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                        {phase.note}
                      </>
                    ) : phase.kind === "error" ? (
                      "Try again"
                    ) : (
                      (confirmation?.payLabel ?? "Pay")
                    )}
                  </Button>
                )}
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </FormSheetContent>
      </FormSheet>
      <AddMyCardSheet open={addOpen} onOpenChange={setAddOpen} makeDefault />
    </>
  );
}
